import * as THREE from 'three/webgpu';
import type { Viewer } from '../../viewer/Viewer';
import {
    MeasureDxDyDzLineRenderMode,
    type MeasureDxDyDzLineRenderInput,
} from '../types/measure-callout-types';
import { MEASURE_DX_DY_DZ_LINE_RENDER_ORDER } from '../utils/measureOverlayRenderLayering';
import {
    createMeasureDashedLine,
    writeMeasureDashedLinePositions,
    type MeasureScreenSpaceDashedLine,
} from '../utils/createMeasureDashedLine';
import {
    hasRenderableMeasureSegmentLength,
    MeasureDxDyDzRenderableSegmentKind,
    resolveRenderableDxDyDzSegments,
} from '../utils/measureDxDyDzRenderableSegments';

/** 两点直线距离线默认颜色（黑）。 */
const MEASURE_DISTANCE_LINE_COLOR = 0x000000;

/** ΔX 分量线默认颜色（红）。 */
const MEASURE_DX_LINE_COLOR = 0xff0000;

/** ΔY 分量线默认颜色（绿）。 */
const MEASURE_DY_LINE_COLOR = 0x00ff00;

/** ΔZ 分量线默认颜色（蓝）。 */
const MEASURE_DZ_LINE_COLOR = 0x0000ff;

/**
 * 单条测量线槽位（复用几何与材质，避免高频重建）。
 */
interface MeasureLineSlot {
    /** 线段对象；未创建时为 null。 */
    line: MeasureScreenSpaceDashedLine | null;
    /** 线段颜色。 */
    color: number;
}

/**
 * ΔX / ΔY / ΔZ 及直线距离测量线三维渲染器。
 *
 * 职责：
 * 1. 按渲染模式绘制黑色直线距离虚线与/或红绿蓝轴对齐阶梯分量虚线；
 * 2. 支持 Hidden / DistanceOnly / DxOnly / DyOnly / DzOnly / XyzOnly / Full 可见性组合；
 * 3. 虚线疏密与线宽按屏幕空间表达，缩放视图时视觉观感稳定；
 * 4. 支持增量更新与统一资源释放。
 */
export class MeasureDxDyDzLineRenderer {
    /** Viewer 实例。 */
    private readonly viewer: Viewer;

    /** 测量线 overlay 根节点。 */
    private overlayRoot: THREE.Group | null = null;

    /** 直线距离线槽位。 */
    private readonly distanceSlot: MeasureLineSlot = {
        line: null,
        color: MEASURE_DISTANCE_LINE_COLOR,
    };

    /** ΔX 分量线槽位。 */
    private readonly dxSlot: MeasureLineSlot = {
        line: null,
        color: MEASURE_DX_LINE_COLOR,
    };

    /** ΔY 分量线槽位。 */
    private readonly dySlot: MeasureLineSlot = {
        line: null,
        color: MEASURE_DY_LINE_COLOR,
    };

    /** ΔZ 分量线槽位。 */
    private readonly dzSlot: MeasureLineSlot = {
        line: null,
        color: MEASURE_DZ_LINE_COLOR,
    };

    /** 当前渲染模式（默认隐藏，由结果区点选驱动）。 */
    private renderMode: MeasureDxDyDzLineRenderMode = MeasureDxDyDzLineRenderMode.Hidden;

    /**
     * @param viewer Viewer 实例。
     * @param renderMode 初始渲染模式；默认 {@link MeasureDxDyDzLineRenderMode.Hidden}。
     */
    public constructor(
        viewer: Viewer,
        renderMode: MeasureDxDyDzLineRenderMode = MeasureDxDyDzLineRenderMode.Hidden,
    ) {
        this.viewer = viewer;
        this.renderMode = renderMode;
    }

    /**
     * 设置测量线渲染模式。
     *
     * @param mode 渲染模式。
     */
    public setRenderMode(mode: MeasureDxDyDzLineRenderMode): void {
        this.renderMode = mode;
    }

    /**
     * 获取当前渲染模式。
     *
     * @returns 渲染模式。
     */
    public getRenderMode(): MeasureDxDyDzLineRenderMode {
        return this.renderMode;
    }

    /**
     * 更新或清空测量线显示。
     *
     * @param input 渲染输入；为 null 时隐藏并移除全部线段。
     * @param mode 本次更新使用的渲染模式；省略时沿用 {@link setRenderMode} 设置的值。
     */
    public setMeasureLines(
        input: MeasureDxDyDzLineRenderInput | null,
        mode?: MeasureDxDyDzLineRenderMode,
    ): void {
        this.ensureOverlayRoot();

        if (!input || !this.overlayRoot) {
            this.hideAllLines();
            this.viewer.requestRender?.();
            return;
        }

        const activeMode = mode ?? this.renderMode;
        if (activeMode === MeasureDxDyDzLineRenderMode.Hidden) {
            this.hideAllLines();
            this.viewer.requestRender?.();
            return;
        }

        const { from, to } = input;
        const showDistance = activeMode === MeasureDxDyDzLineRenderMode.DistanceOnly
            || activeMode === MeasureDxDyDzLineRenderMode.Full;
        const showDx = activeMode === MeasureDxDyDzLineRenderMode.DxOnly
            || activeMode === MeasureDxDyDzLineRenderMode.XyzOnly
            || activeMode === MeasureDxDyDzLineRenderMode.Full;
        const showDy = activeMode === MeasureDxDyDzLineRenderMode.DyOnly
            || activeMode === MeasureDxDyDzLineRenderMode.XyzOnly
            || activeMode === MeasureDxDyDzLineRenderMode.Full;
        const showDz = activeMode === MeasureDxDyDzLineRenderMode.DzOnly
            || activeMode === MeasureDxDyDzLineRenderMode.XyzOnly
            || activeMode === MeasureDxDyDzLineRenderMode.Full;

        if (showDistance) {
            this.upsertSegmentLine(this.distanceSlot, from, to);
        } else {
            this.disposeLineSlot(this.distanceSlot);
        }

        if (showDx || showDy || showDz) {
            const { dx, dy, dz } = input;
            const segments = resolveRenderableDxDyDzSegments({
                from,
                to,
                dx,
                dy,
                dz,
            });
            if (showDx) {
                this.syncComponentLineSlot(MeasureDxDyDzRenderableSegmentKind.Dx, this.dxSlot, segments);
            } else {
                this.disposeLineSlot(this.dxSlot);
            }
            if (showDy) {
                this.syncComponentLineSlot(MeasureDxDyDzRenderableSegmentKind.Dy, this.dySlot, segments);
            } else {
                this.disposeLineSlot(this.dySlot);
            }
            if (showDz) {
                this.syncComponentLineSlot(MeasureDxDyDzRenderableSegmentKind.Dz, this.dzSlot, segments);
            } else {
                this.disposeLineSlot(this.dzSlot);
            }
        } else {
            this.disposeLineSlot(this.dxSlot);
            this.disposeLineSlot(this.dySlot);
            this.disposeLineSlot(this.dzSlot);
        }

        this.overlayRoot.visible = true;
        this.viewer.requestRender?.();
    }

    /**
     * 清空测量线并隐藏 overlay。
     */
    public clear(): void {
        this.hideAllLines();
        this.viewer.requestRender?.();
    }

    /**
     * 释放 overlay 与全部线段资源。
     */
    public dispose(): void {
        this.clear();
        if (this.overlayRoot) {
            if (this.overlayRoot.parent) {
                this.overlayRoot.parent.remove(this.overlayRoot);
            }
            this.overlayRoot = null;
        }
    }

    /**
     * 确保测量线 overlay 根节点已挂载到场景。
     */
    private ensureOverlayRoot(): void {
        if (this.overlayRoot) {
            return;
        }

        const overlayRoot = new THREE.Group();
        overlayRoot.name = 'MeasureDxDyDzLineOverlayRoot';
        overlayRoot.visible = false;
        overlayRoot.userData.skipSelection = true;
        overlayRoot.userData.skipSelectionSubtree = true;
        this.viewer.addObject(overlayRoot);
        this.overlayRoot = overlayRoot;
    }

    /**
     * 更新或隐藏单条线段。
     *
     * @param slot 线段槽位。
     * @param start 起点。
     * @param end 终点。
     */
    private upsertSegmentLine(
        slot: MeasureLineSlot,
        start: THREE.Vector3,
        end: THREE.Vector3,
    ): void {
        if (!this.overlayRoot) {
            return;
        }

        if (!hasRenderableMeasureSegmentLength(start, end)) {
            this.disposeLineSlot(slot);
            return;
        }

        if (!slot.line) {
            slot.line = createMeasureDashedLine(
                slot.color,
                MEASURE_DX_DY_DZ_LINE_RENDER_ORDER,
                'MeasureDxDyDzLine',
            );
            this.overlayRoot.add(slot.line);
        }

        writeMeasureDashedLinePositions(slot.line, start, end);
        slot.line.visible = true;
    }

    /**
     * 同步单个分量线槽位。
     *
     * @param kind 分量线种类。
     * @param slot 线段槽位。
     * @param segments 本帧可展示分量线。
     */
    private syncComponentLineSlot(
        kind: MeasureDxDyDzRenderableSegmentKind,
        slot: MeasureLineSlot,
        segments: ReturnType<typeof resolveRenderableDxDyDzSegments>,
    ): void {
        const segment = segments.find((candidate) => candidate.kind === kind);
        if (!segment) {
            this.disposeLineSlot(slot);
            return;
        }
        this.upsertSegmentLine(slot, segment.start, segment.end);
    }

    /**
     * 隐藏全部线段并释放槽位对象。
     */
    private hideAllLines(): void {
        this.disposeLineSlot(this.distanceSlot);
        this.disposeLineSlot(this.dxSlot);
        this.disposeLineSlot(this.dySlot);
        this.disposeLineSlot(this.dzSlot);
        if (this.overlayRoot) {
            this.overlayRoot.visible = false;
        }
    }

    /**
     * 释放单槽位线段资源。
     *
     * @param slot 线段槽位。
     */
    private disposeLineSlot(slot: MeasureLineSlot): void {
        if (!slot.line) {
            return;
        }

        if (slot.line.parent) {
            slot.line.parent.remove(slot.line);
        }
        slot.line.geometry.dispose();
        disposeLineMaterial(slot.line.material);
        slot.line = null;
    }
}

/**
 * 释放线段材质。
 *
 * @param material 线段材质或材质数组。
 */
function disposeLineMaterial(material: THREE.Material | THREE.Material[]): void {
    if (Array.isArray(material)) {
        material.forEach((item) => item.dispose());
        return;
    }
    material.dispose();
}
